/**
 * Stepper visuel des 7 étapes du booking client. Affiché en haut de la page
 * /projects/:id (hub de suivi) pour guider l'utilisateur vers la prochaine
 * action.
 *
 * Statuts visuels :
 *  - 'done'    → cercle vert avec coche
 *  - 'current' → cercle bleu pulse
 *  - 'todo'    → cercle gris
 */
import { Check, Loader2 } from 'lucide-react';
import type { BookingStep } from './booking-progress';
import { cn } from '@/lib/utils';

export function BookingStepper({ steps }: { steps: BookingStep[] }) {
  return (
    <ol
      aria-label="Étapes du dossier"
      className="flex flex-wrap items-start gap-2 sm:gap-3"
    >
      {steps.map((step, index) => (
        <li
          key={step.key}
          data-step-key={step.key}
          data-step-status={step.status}
          className="flex flex-1 min-w-[110px] flex-col items-center gap-1"
        >
          <div className="flex w-full items-center">
            <div className={dotClasses(step.status)} aria-hidden="true">
              {step.status === 'done' ? (
                <Check className="h-4 w-4" />
              ) : step.status === 'current' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <span className="text-xs">{index + 1}</span>
              )}
            </div>
            {index < steps.length - 1 && (
              <div
                className={cn(
                  'mx-1 h-0.5 flex-1 rounded-full',
                  step.status === 'done' ? 'bg-emerald-400' : 'bg-gray-200',
                )}
              />
            )}
          </div>
          <span className={labelClasses(step.status)}>{step.label}</span>
        </li>
      ))}
    </ol>
  );
}

function dotClasses(status: BookingStep['status']): string {
  return cn(
    'grid h-8 w-8 shrink-0 place-items-center rounded-full text-white',
    status === 'done' && 'bg-emerald-500',
    status === 'current' && 'bg-bcl-blue',
    status === 'todo' && 'bg-gray-300 text-gray-600',
  );
}

function labelClasses(status: BookingStep['status']): string {
  return cn(
    'text-center text-xs font-medium leading-tight',
    status === 'done' && 'text-emerald-700',
    status === 'current' && 'text-bcl-blue',
    status === 'todo' && 'text-gray-500',
  );
}
